import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, LogOut } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api/config';

// Tela pós-login onde o usuário escolhe em qual instituição vai trabalhar
// (ver InstitutionContext — a escolha feita aqui vira o header x-institution-id
// enviado em toda chamada de API subsequente).
// Tela inicial por perfil, após escolher a instituição — cada perfil já cai
// direto na tela que mais usa no dia a dia, em vez de sempre abrir a Chamada:
// educador (professor) vê a Grade dele, monitor vê a Chamada (comportamento
// de sempre), master/coordenador veem o Relatório (visão gerencial).
// Perfis fora dessa lista (nenhum hoje, mas por segurança) caem na Chamada.
const TELA_INICIAL_POR_PERFIL = {
  professor: '/grade',
  monitor: '/',
  master: '/relatorio-diario',
  coordenador: '/relatorio-diario',
};

function SelecionarInstituicao() {
  const { selectInstitution } = useInstitution();
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/instituicoes/todas`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((body) => {
        const data = body?.data ?? (Array.isArray(body) ? body : []);
        setInstituicoes(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erro ao buscar instituições:', err);
        setLoading(false);
      });
  }, [token]);

  const handleSelect = async (id, nome) => {
    selectInstitution(id, nome);

    if (user?.perfil === 'master') {
      navigate('/relatorio-diario');
      return;
    }

    try {
      // Busca as permissões da instituição selecionada para ir direto pra primeira tela liberada
      const res = await fetch(`${API_URL}/api/auth/minhas-permissoes`, {
        headers: {
          'x-institution-id': id,
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await res.json();
      const telas = body?.data?.telas_permitidas ?? body?.telas_permitidas ?? [];

      const destinoPadrao = TELA_INICIAL_POR_PERFIL[user?.perfil];
      if (destinoPadrao && telas.includes(destinoPadrao)) {
        navigate(destinoPadrao);
      } else if (telas.length > 0) {
        // Abre na primeira tela que estiver liberada para o perfil
        navigate(telas[0]);
      } else {
        navigate(destinoPadrao || '/');
      }
    } catch {
      // Em caso de erro de rede ou mock em teste, usa o mapa padrão
      navigate(TELA_INICIAL_POR_PERFIL[user?.perfil] || '/');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Selecione sua Instituição
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Escolha a unidade em que está trabalhando hoje
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-3" />
              <span className="text-sm">Carregando instituições...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {instituicoes.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => handleSelect(inst.id, inst.nome)}
                  className="group flex items-center gap-4 p-2.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all text-left"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50 flex items-center justify-center transition-colors">
                    <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span
                    className="text-base font-medium text-gray-900 dark:text-gray-100"
                    translate="no"
                  >
                    {inst.nome}
                  </span>
                </button>
              ))}
            </div>
          )}

          {!loading && instituicoes.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
              <p className="text-sm">Nenhuma instituição encontrada.</p>
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SelecionarInstituicao;
